// Models
import {Beat, emptyBeat, setPoint} from "./model"

/**
 * The analysis model's own file: `predictions_global.csv`.
 *
 * One row per WAVE, not per beat, and each mark applies to all twelve leads —
 * which is the global envelope this screen was built to edit, arriving as a
 * file instead of over an endpoint.
 *
 *     wave,onset_sample,offset_sample,onset_ms,offset_ms,duration_ms
 *     P,42,90,84,180,96
 *     QRS,106,148,212,296,84
 *     T,206,312,412,624,212
 *
 * CONVENTIONS, taken from the README rather than assumed:
 *   - sample indices are counted from ZERO, and `time_ms = sample * 2` at
 *     500 Hz. This model's own CSV writer counts samples from ONE, so reading
 *     or writing this file through that would move every mark by a sample.
 *     Everything here uses the file's convention, because the file has to come
 *     back in the shape it went out.
 *   - `onset` starts a wave, `offset` ends it.
 *   - the global file has no peak and no R peak. The beat is anchored on the
 *     middle of its QRS, which is only a handle for the view — nothing is
 *     written back from it.
 *
 * The cardiologists' own marks in the LUDB bundle are PER LEAD and are not this
 * file; they are a separate comparison, not something to load here.
 */

export type WaveName = "P" | "QRS" | "T";

type Row = {wave: WaveName, onsetMs: number, offsetMs: number};

/** Sample index to milliseconds, counted from zero as the file does. */
export function sampleToMsZeroBased(sample: number, sampleRate: number): number {
    return (sample * 1000) / sampleRate
}

/** Milliseconds back to a zero-based sample index. */
export function msToSampleZeroBased(ms: number, sampleRate: number): number {
    return Math.round((ms * sampleRate) / 1000)
}

function splitRow(line: string): string[] {
    return line.split(",").map(cell => cell.trim())
}

/**
 * Read the rows, trusting the millisecond columns.
 *
 * Both the sample and the millisecond columns are present and agree, so either
 * would do — the millisecond ones are taken because they are what the rest of
 * this module works in, and converting is then one fewer place to be wrong.
 *
 * @param text
 * @param sampleRate used only when the ms columns are absent
 */
function readRows(text: string, sampleRate: number): Row[] {
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");

    if (!lines.length) {
        return []
    }

    const header = splitRow(lines[0]).map(cell => cell.toLowerCase());
    const waveAt = header.indexOf("wave");
    const onsetMsAt = header.indexOf("onset_ms");
    const offsetMsAt = header.indexOf("offset_ms");
    const onsetAt = header.indexOf("onset_sample");
    const offsetAt = header.indexOf("offset_sample");

    if (waveAt === -1 || (onsetMsAt === -1 && onsetAt === -1)) {
        return []
    }

    const rows: Row[] = [];

    lines.slice(1).forEach(line => {
        const cells = splitRow(line);
        const wave = (cells[waveAt] || "").toUpperCase();

        if (wave !== "P" && wave !== "QRS" && wave !== "T") {
            return
        }

        const onsetMs = onsetMsAt !== -1
            ? Number(cells[onsetMsAt])
            : sampleToMsZeroBased(Number(cells[onsetAt]), sampleRate);
        const offsetMs = offsetMsAt !== -1
            ? Number(cells[offsetMsAt])
            : sampleToMsZeroBased(Number(cells[offsetAt]), sampleRate);

        if (!isFinite(onsetMs) || !isFinite(offsetMs)) {
            return
        }

        rows.push({wave: wave as WaveName, onsetMs: onsetMs, offsetMs: offsetMs})
    });

    return rows.sort((a, b) => a.onsetMs - b.onsetMs)
}

/**
 * Group the waves into beats.
 *
 * The QRS defines a beat — it is the one wave that is always there, and it is
 * what a reader counts. Each QRS then takes the P that sits closest before it
 * and the T that sits closest after it, bounded by the neighbouring QRS so a
 * wave can never be claimed by two beats.
 *
 * Grouping by position — assuming the rows run P, QRS, T forever — would be
 * simpler and wrong: a beat with no P, or a stray P at the end of the record,
 * would shift every later beat by one.
 *
 * @param text the file's contents
 * @param sampleRate
 */
export function parseGlobalCsv(text: string, sampleRate: number): Beat[] {
    const rows = readRows(text, sampleRate);
    const qrs = rows.filter(row => row.wave === "QRS");

    return qrs.map((complex, index) => {
        const previous = index > 0 ? qrs[index - 1] : null;
        const next = index < qrs.length - 1 ? qrs[index + 1] : null;

        const p = rows
            .filter(row => row.wave === "P" &&
                row.offsetMs <= complex.onsetMs &&
                (previous === null || row.onsetMs > previous.offsetMs))
            .pop() || null;

        const t = rows
            .filter(row => row.wave === "T" &&
                row.onsetMs >= complex.offsetMs &&
                (next === null || row.offsetMs < next.onsetMs))
            .shift() || null;

        /* the file carries no R peak; the middle of the QRS is a handle for the
           view to centre on and is never written back */
        let beat = emptyBeat(index + 1, Math.round((complex.onsetMs + complex.offsetMs) / 2));

        beat = setPoint(beat, "pStart", p ? p.onsetMs : null);
        beat = setPoint(beat, "pEnd", p ? p.offsetMs : null);
        beat = setPoint(beat, "qrsStart", complex.onsetMs);
        beat = setPoint(beat, "qrsEnd", complex.offsetMs);
        beat = setPoint(beat, "tStart", t ? t.onsetMs : null);
        beat = setPoint(beat, "tEnd", t ? t.offsetMs : null);

        /* nothing here has been reviewed — these are the model's marks */
        return Object.assign({}, beat, {status: "auto" as const, reviewedAt: null})
    })
}
